import { RefreshCcw, Rocket, Cpu, LockKeyhole } from "lucide-react";
import { SectionHead, Stagger, StaggerItem } from "@/components/Reveal";

const CASES = [
  {
    icon: RefreshCcw,
    title: "Digital Transformation",
    for: "Traditional businesses modernizing their infrastructure",
    solution: "Cloud migration, legacy app modernization",
    benefit: "Accelerate innovation",
  },
  {
    icon: Rocket,
    title: "Startup Scale-Up",
    for: "Growing startups needing elastic infrastructure",
    solution: "Cloud-native architecture, cost optimization",
    benefit: "Scale without over-provisioning",
  },
  {
    icon: Cpu,
    title: "High-Performance Computing",
    for: "Data-intensive and compute-intensive workloads",
    solution: "GPU compute, distributed processing, big data",
    benefit: "Fast time-to-insight",
  },
  {
    icon: LockKeyhole,
    title: "Security & Compliance",
    for: "Regulated industries — finance, healthcare, and more",
    solution: "Compliant cloud infrastructure, security automation",
    benefit: "Meet regulatory requirements with confidence",
  },
];

export default function UseCases() {
  return (
    <section id="use-cases" data-testid="use-cases-section" className="py-24 sm:py-32 bg-nexlight">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHead
          chapter="04"
          overline="Use Cases"
          title="Solutions by Business Need"
          sub="Your growth deserves powerful infrastructure — shaped around the problem you're actually solving."
          testId="use-cases-heading"
        />
        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {CASES.map(({ icon: Icon, title, ...rows }, i) => (
            <StaggerItem key={title} className={i % 2 === 1 ? "md:mt-12" : ""}>
              <div
                data-testid={`use-case-card-${i + 1}`}
                className="group relative h-full overflow-hidden rounded-2xl border border-nexblue/10 bg-white p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
              >
                <span className="absolute -right-4 -top-6 font-display text-[110px] font-bold leading-none text-nexblue/[0.05] select-none" aria-hidden="true">
                  0{i + 1}
                </span>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-nexblue text-nexorange transition-colors duration-300 group-hover:bg-nexorange group-hover:text-nexblue">
                  <Icon size={22} />
                </div>
                <h3 className="mt-6 font-display text-xl sm:text-2xl font-semibold text-nexblue">{title}</h3>
                <dl className="mt-6 space-y-4">
                  {[["For", rows.for], ["Solution", rows.solution], ["Key Benefit", rows.benefit]].map(([k, v]) => (
                    <div key={k} className="flex gap-4">
                      <dt className="w-24 shrink-0 text-xs font-bold uppercase tracking-[0.15em] text-nexcyan pt-0.5">{k}</dt>
                      <dd className="text-sm leading-relaxed text-nexink/75">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
